import { Button } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useState } from 'react';
import { TbPencil, TbPlus } from 'react-icons/tb';
import { toast } from 'react-toastify';
import ConfirmModal from '../../custom-components/ConfirmModal';
import LoadingBars from '../../custom-components/LoadingBars';
import { invalidateTerritoriesCache } from '../../custom-components/QueryEditor';
import { RemoveButton } from '../../custom-components/RemoveButton';
import UniversalTag from '../../custom-components/UniversalTag';
import { toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { isAdditiveClick } from './BeaconList';
import { PanelInfo } from './PanelInfo';

/** What a territory is and how it is used, behind the list's info icon. */
const TERRITORIES_INFO =
  "A territory is a named set of states your team covers, such as a rep's region. Select one " +
  'to see it on the map, then click states to add or remove them; Save keeps the changes. ' +
  'People search can filter on a territory.';

/** The name a new territory gets; the form renames it. */
const NEW_TERRITORY_NAME = 'Untitled territory';

interface TerritoryListProps {
  territories: Edulead.EduleadV4Territory[];
  isLoading: boolean;
  /** Opens the territory's form. */
  onOpen: (id: string) => void;
  /** Selects the territory (shown on the map), or with isAdditive adds or takes it out. */
  onSelect: (id: string, isAdditive: boolean) => void;
  selectedIds: string[];
  onCreated: (territory: Edulead.EduleadV4Territory) => void;
  onDeleted: (id: string) => void;
}

/**
 * The team's territories: select one to see and edit its states on the map, open one to rename
 * it and list its states, create an empty one, or delete one.
 */
export function TerritoryList({
  territories,
  isLoading,
  onOpen,
  onSelect,
  selectedIds,
  onCreated,
  onDeleted,
}: TerritoryListProps) {
  const [isCreating, setIsCreating] = useState(false);
  // The territory whose delete is being confirmed, and whether the delete is under way.
  const [toDelete, setToDelete] = useState<Edulead.EduleadV4Territory | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /** Creates an empty territory and opens it, so its states can be clicked on the map. */
  const handleCreate = async () => {
    try {
      setIsCreating(true);
      const { territory } = await eduleadV4.territories.create({
        request_id: crypto.randomUUID(),
        body: { name: NEW_TERRITORY_NAME, states: [] },
      });
      invalidateTerritoriesCache();
      if (territory?.id) {
        onCreated(territory);
        onOpen(territory.id);
      }
    } catch (error) {
      toastV4Error(error, 'Failed to create territory');
    } finally {
      setIsCreating(false);
    }
  };

  /** Deletes the territory the dialog confirmed. */
  const confirmDelete = async () => {
    if (!toDelete?.id) return;
    try {
      setIsDeleting(true);
      await eduleadV4.territories.delete({ id: toDelete.id });
      invalidateTerritoriesCache();
      toast.success('Territory deleted');
      onDeleted(toDelete.id);
      setToDelete(null);
    } catch (error) {
      toastV4Error(error, 'Failed to delete territory');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="beacon-list">
      <div className="navigator-beacons-panel__bar beacon-list__header">
        <span>Territories</span>
        <span className="navigator-beacons-panel__bar-actions">
          <PanelInfo text={TERRITORIES_INFO} />
          <Button
            size="small"
            className="nohover tiny"
            aria-label="New territory"
            isDisabled={isCreating}
            onClick={handleCreate}
          >
            <TbPlus />
          </Button>
        </span>
      </div>
      {isLoading ? (
        <LoadingBars numberOfRows={4} />
      ) : territories.length === 0 ? (
        <div className="beacon-list__empty">
          A territory is a set of states. Create one and click states on the map.
        </div>
      ) : (
        territories.map((territory) => (
          <div
            key={territory.id}
            className={`beacon-list__item ${territory.id && selectedIds.includes(territory.id) ? 'beacon-list__item--selected' : ''}`}
            onClick={(e) => territory.id && onSelect(territory.id, isAdditiveClick(e))}
          >
            <div className="beacon-list__item-text">
              <span
                className="territory-list__swatch"
                style={{ backgroundColor: `var(--territory-color-${territory.color ?? 1})` }}
              />
              <div className="beacon-list__name">{territory.name}</div>
              <UniversalTag
                category={(territory.states?.length ?? 0).toLocaleString()}
                classOverride="tag-neutral"
              />
            </div>
            {/* A click on a button edits or deletes; it must not also select the territory. */}
            <div className="beacon-list__actions" onClick={(e) => e.stopPropagation()}>
              <Button
                className="nohover tiny beacon-list__edit"
                aria-label="Edit territory"
                onClick={() => territory.id && onOpen(territory.id)}
              >
                <TbPencil />
              </Button>
              <RemoveButton
                title="Delete territory"
                isTitleHidden
                className="nohover"
                onClick={() => setToDelete(territory)}
              />
            </div>
          </div>
        ))
      )}
      <ConfirmModal
        isOpen={toDelete !== null}
        onClose={() => !isDeleting && setToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete territory?"
        message={`"${toDelete?.name ?? ''}" is deleted. This cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </div>
  );
}
